<template>
  <div class="saldo-devolucao-page">
    <div class="d-flex align-items-center justify-content-between mb-1">
      <nav class="page-breadcrumb">
        <router-link :to="{ name: 'dashboard-ptc' }" class="breadcrumb-link">
          <feather-icon icon="HomeIcon" size="14" />
          <span>Devoluções e Trocas</span>
        </router-link>
        <feather-icon icon="ChevronRightIcon" size="14" class="breadcrumb-separator mx-25" />
        <span class="breadcrumb-current">Saldo de Devolução</span>
      </nav>
      <b-button
        v-if="showTutorial"
        variant="outline-primary"
        size="sm"
        @click="$bvModal.show('modal-tutorial-devolucao')"
      >
        <feather-icon icon="BookOpenIcon" size="14" class="mr-50" />
        Guia de Devolução
      </b-button>
    </div>

    <!-- Filters -->
    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col md="3" sm="6">
          <label class="small text-muted">Cód. Emitente</label>
          <b-form-input v-model="filters.cod_emitente" size="sm" placeholder="Ex: 40854" @keydown.enter="buscar" />
        </b-col>
        <b-col md="3" sm="6">
          <label class="small text-muted">Nº Contrato</label>
          <b-form-input v-model="filters.nr_contrato" size="sm" placeholder="Contrato" @keydown.enter="buscar" />
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Ativo</label>
          <b-form-select v-model="filters.ativo" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option :value="1">Sim</b-form-select-option>
            <b-form-select-option :value="0">Não</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Considera</label>
          <b-form-select v-model="filters.considera" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option :value="1">Sim</b-form-select-option>
            <b-form-select-option :value="0">Não</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Tipo</label>
          <b-form-select v-model="filtroTipo" size="sm">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option :value="1">Didático</b-form-select-option>
            <b-form-select-option :value="2">Paradidático</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Fat. de</label>
          <b-form-input v-model="filtroFatDe" type="date" size="sm" />
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Fat. até</label>
          <b-form-input v-model="filtroFatAte" type="date" size="sm" />
        </b-col>
        <b-col md="2" sm="6" class="d-flex align-items-end" style="gap:6px">
          <b-button size="sm" variant="primary" @click="buscar" :disabled="isLoading">
            <feather-icon icon="SearchIcon" size="13" />
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
            <feather-icon icon="XIcon" size="13" />
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table -->
    <b-card>
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner />
      </div>
      <div v-else-if="!saldosFiltrados.length" class="text-center py-4 text-muted">
        <feather-icon icon="InboxIcon" size="36" class="mb-1" /><br>
        Nenhum saldo de devolução encontrado.
      </div>
      <b-table
        v-else
        :items="saldosFiltrados"
        :fields="fields"
        hover
        responsive
        small
        sticky-header="60vh"
      >
        <template #cell(cod_emitente)="{ item }">
          <strong>{{ item.cod_emitente }}</strong>
        </template>
        <template #cell(cliente_nome)="{ item }">
          <span :title="item.cliente_nome || ''">{{ item.nome_abrev || item.cliente_nome || '—' }}</span>
        </template>
        <template #cell(ativo)="{ item }">
          <b-badge :variant="item.ativo ? 'light-success' : 'light-secondary'">
            {{ item.ativo ? 'Sim' : 'Não' }}
          </b-badge>
        </template>
        <template #cell(considera)="{ item }">
          <b-badge :variant="item.considera ? 'light-info' : 'light-secondary'">
            {{ item.considera ? 'Sim' : 'Não' }}
          </b-badge>
        </template>
        <template #cell(tipo_devol)="{ item }">
          <b-badge :variant="tipoDevolVariant(item.tipo_devol)">
            {{ formatTipoDevol(item.tipo_devol) }}
          </b-badge>
        </template>
        <template #cell(periodo_fat_ini)="{ item }">{{ formatDate(item.periodo_fat_ini) }}</template>
        <template #cell(periodo_fat_fim)="{ item }">{{ formatDate(item.periodo_fat_fim) }}</template>
        <template #cell(periodo_devol_ini)="{ item }">{{ formatDate(item.periodo_devol_ini) }}</template>
        <template #cell(periodo_devol_fim)="{ item }">{{ formatDate(item.periodo_devol_fim) }}</template>
        <template #cell(perc_devol)="{ item }">{{ formatPerc(item.perc_devol) }}</template>
        <template #cell(vl_total_fatur)="{ item }">{{ formatMoney(item.vl_total_fatur) }}</template>
        <template #cell(vl_total_devol)="{ item }">{{ formatMoney(item.vl_total_devol) }}</template>
        <template #cell(vl_devol_confirmado)="{ item }">{{ formatMoney(item.vl_devol_confirmado) }}</template>
        <template #cell(vl_devol_pendente)="{ item }">{{ formatMoney(item.vl_devol_pendente) }}</template>
        <template #cell(vl_saldo_confirmado)="{ item }">{{ formatMoney(item.vl_saldo_confirmado) }}</template>
        <template #cell(vl_saldo_final)="{ item }">
          <strong>{{ formatMoney(item.vl_saldo_final) }}</strong>
        </template>
        <template #cell(acoes)="{ item }">
          <b-button
            variant="flat-primary"
            size="sm"
            class="btn-icon btn-olho-saldo"
            @click="visualizar(item)"
          >
            <feather-icon icon="EyeIcon" size="16" />
          </b-button>
        </template>
      </b-table>

      <modal-visualizar-saldo :saldo="saldoSelecionado" @hidden="saldoSelecionado = null" />
      <modal-tutorial-devolucao v-if="showTutorial" />

      <div v-if="meta.totalPages > 1" class="d-flex justify-content-end mt-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="meta.pageSize"
          size="sm"
          @change="onPageChange"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ModalVisualizarSaldo from './modal/ModalVisualizarSaldo.vue';
import ModalTutorialDevolucao from './modal/ModalTutorialDevolucao.vue';

export default {
  name: 'SaldoDevolucao',
  components: { ModalVisualizarSaldo, ModalTutorialDevolucao },
  data() {
    return {
      filters: { cod_emitente: '', nr_contrato: '', ativo: 1, considera: null },
      // Filtros aplicados client-side: o endpoint /devolucao-saldo não aceita
      // tipo nem período de faturamento, então filtramos sobre a página atual.
      filtroTipo: null,
      filtroFatDe: '',
      filtroFatAte: '',
      currentPage: 1,
      fields: [
        { key: 'cod_emitente',        label: 'Emitente',         sortable: false },
        { key: 'cliente_nome',        label: 'Cliente',          sortable: false },
        { key: 'nr_contrato',         label: 'Contrato',         sortable: false },
        { key: 'sequencia',           label: 'Seq.',             sortable: false },
        { key: 'tipo_devol',          label: 'Tipo',             sortable: false },
        { key: 'ativo',               label: 'Ativo',            sortable: false },
        { key: 'considera',           label: 'Considera',        sortable: false },
        { key: 'perc_devol',          label: '% Devol.',         sortable: false },
        { key: 'cod_lista',           label: 'Lista',            sortable: false },
        { key: 'periodo_fat_ini',     label: 'Fat. Início',      sortable: false },
        { key: 'periodo_fat_fim',     label: 'Fat. Fim',         sortable: false },
        { key: 'periodo_devol_ini',   label: 'Devol. Início',    sortable: false },
        { key: 'periodo_devol_fim',   label: 'Devol. Fim',       sortable: false },
        { key: 'vl_total_fatur',      label: 'Total Faturado',   sortable: false, class: 'text-right' },
        { key: 'vl_total_devol',      label: 'Limite Permitido', sortable: false, class: 'text-right' },
        { key: 'vl_devol_confirmado', label: 'Devol. Confirm.',  sortable: false, class: 'text-right' },
        { key: 'vl_devol_pendente',   label: 'Devol. Pendente',  sortable: false, class: 'text-right' },
        { key: 'vl_saldo_confirmado', label: 'Saldo Confirm.',   sortable: false, class: 'text-right' },
        { key: 'vl_saldo_final',      label: 'Saldo Final',      sortable: false, class: 'text-right' },
        { key: 'acoes',               label: 'Ações',            sortable: false, class: 'text-center', stickyColumn: true },
      ],
      saldoSelecionado: null,
    };
  },
  computed: {
    ...mapGetters('DevolucaoSaldo', ['isLoading', 'saldos', 'meta']),
    // Aplica os filtros de tipo e período de faturamento client-side sobre as
    // linhas carregadas (o back é paginado e não aceita esses filtros).
    saldosFiltrados() {
      let lista = this.saldos || [];
      if (this.filtroTipo !== null && this.filtroTipo !== '') {
        lista = lista.filter((s) => Number(s.tipo_devol) === Number(this.filtroTipo));
      }
      if (this.filtroFatDe) {
        const de = this.filtroFatDe;
        lista = lista.filter((s) => s.periodo_fat_ini && String(s.periodo_fat_ini).slice(0, 10) >= de);
      }
      if (this.filtroFatAte) {
        const ate = this.filtroFatAte;
        lista = lista.filter((s) => s.periodo_fat_fim && String(s.periodo_fat_fim).slice(0, 10) <= ate);
      }
      return lista;
    },
    // O guia de devolução é específico da Macmillan (e-mails, CNPJ e endereço da LUFT).
    showTutorial() {
      const slug = (this.$route.params.tenantSlug || localStorage.getItem('tenantSlug') || '').toLowerCase().trim();
      return slug === 'macmillan';
    },
  },
  created() {
    this.buscar();
  },
  methods: {
    ...mapActions('DevolucaoSaldo', ['listar', 'setFilters', 'clearFilters']),
    async buscar() {
      await this.setFilters({ ...this.filters, page: this.currentPage });
      await this.listar();
    },
    async limparFiltros() {
      this.filters = { cod_emitente: '', nr_contrato: '', ativo: 1, considera: null };
      this.filtroTipo = null;
      this.filtroFatDe = '';
      this.filtroFatAte = '';
      this.currentPage = 1;
      // Zera o store e reaplica o filtro padrão (ativo = Sim) em vez de listar sem filtro.
      await this.clearFilters();
      await this.buscar();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.buscar();
    },
    visualizar(item) {
      this.saldoSelecionado = item;
      this.$nextTick(() => this.$bvModal.show('modal-visualizar-saldo'));
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '—';
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
    formatMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPerc(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    },
    formatTipoDevol(v) {
      const n = Number(v);
      if (n === 1) return 'DIDÁTICO';
      if (n === 2) return 'PARADIDÁTICO';
      return '—';
    },
    tipoDevolVariant(v) {
      const n = Number(v);
      if (n === 1) return 'light-primary';
      if (n === 2) return 'light-warning';
      return 'light-secondary';
    },
  },
};
</script>

<style scoped>
.text-right { text-align: right; }
</style>

<style lang="scss">
// Sticky column (Ações): no modo claro precisa de fundo branco (senão herda escuro e conflita com o print)
.saldo-devolucao-page .b-table-sticky-column {
  background-color: #fff !important;
}
.saldo-devolucao-page .table.b-table > thead > tr > .b-table-sticky-column {
  background-color: #f3f2f7 !important;
}
.saldo-devolucao-page .table.b-table.table-hover > tbody > tr:hover > .b-table-sticky-column {
  background-color: #f8f8f8 !important;
}
// Tema escuro mantém os fundos escuros originais
.dark-layout .saldo-devolucao-page .b-table-sticky-column {
  background-color: #283046 !important;
}
.dark-layout .saldo-devolucao-page .table.b-table > thead > tr > .b-table-sticky-column {
  background-color: #343d55 !important;
}
.dark-layout .saldo-devolucao-page .table.b-table.table-hover > tbody > tr:hover > .b-table-sticky-column {
  background-color: #242b3d !important;
}
// No modo escuro, o botão de olho ficava com fundo branco ao passar o mouse
// na linha; força fundo transparente/roxo translúcido em TODOS os estados.
body.dark-layout .saldo-devolucao-page .btn-olho-saldo,
body.dark-layout .saldo-devolucao-page .table.b-table > tbody > tr:hover .btn-olho-saldo {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  color: #7367f0 !important;
  box-shadow: none !important;
}
body.dark-layout .saldo-devolucao-page .btn-olho-saldo:hover,
body.dark-layout .saldo-devolucao-page .btn-olho-saldo:focus,
body.dark-layout .saldo-devolucao-page .btn-olho-saldo:active,
body.dark-layout .saldo-devolucao-page .btn-olho-saldo.active {
  background: rgba(115, 103, 240, 0.2) !important;
  background-color: rgba(115, 103, 240, 0.2) !important;
  color: #7367f0 !important;
}
</style>
